/**
 * Multiplayer cursor overlay for the pathology viewer.
 *
 * Renders peer cursors as absolutely-positioned SVG arrows with name labels.
 * Uses requestAnimationFrame + direct DOM manipulation for smooth updates
 * without React re-renders per frame.
 */

import { imageToScreen } from "@frontend/pages/case-workspace/pathology/lib/viewer-screen-coords"
import type { PeerInfo } from "@shared/ws/types.ts"
import type { TileViewer } from "@virdx/wsi-viewer"
import { useEffect, useRef } from "react"

interface PresenceCursorsProps {
  peersRef: React.RefObject<Map<string, PeerInfo>>
  viewerRef: React.RefObject<TileViewer | null>
  canvasRef: React.RefObject<HTMLCanvasElement | null>
  peers: PeerInfo[]
}

/** Arrow cursor SVG path (16x16 viewbox) */
const CURSOR_PATH = "M0 0L10 9L0 14Z"

export function PresenceCursors({ peersRef, viewerRef, canvasRef, peers }: PresenceCursorsProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const cursorEls = useRef<Map<string, HTMLDivElement>>(new Map())
  const rafRef = useRef<number>(0)

  // Create/remove cursor DOM elements when peers change (structural changes only)
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const currentIds = new Set(peers.map((p) => p.userId))

    // Remove cursors for peers that left
    for (const [id, el] of cursorEls.current) {
      if (!currentIds.has(id)) {
        el.remove()
        cursorEls.current.delete(id)
      }
    }

    // Add cursors for new peers
    for (const peer of peers) {
      if (cursorEls.current.has(peer.userId)) continue

      const el = document.createElement("div")
      el.style.cssText =
        "position:absolute;left:0;top:0;pointer-events:none;transition:opacity 150ms;opacity:0;will-change:transform"

      el.innerHTML = `
        <svg width="18" height="18" viewBox="-1 -1 18 18" fill="none" style="display:block;margin:-1px 0 0 -1px">
          <path d="${CURSOR_PATH}" fill="${peer.color}" stroke="white" stroke-width="1"/>
        </svg>
        <div style="
          position:absolute;
          left:14px;
          top:12px;
          background:${peer.color};
          color:white;
          font-size:10px;
          line-height:14px;
          padding:1px 5px;
          border-radius:3px;
          white-space:nowrap;
          font-family:system-ui,sans-serif;
          font-weight:500;
        ">${escapeHtml(peer.name)}</div>
      `

      container.appendChild(el)
      cursorEls.current.set(peer.userId, el)
    }
  }, [peers])

  // rAF loop: convert image coords → screen coords and update transforms
  useEffect(() => {
    const tick = () => {
      if (cursorEls.current.size === 0) {
        rafRef.current = requestAnimationFrame(tick)
        return
      }

      const viewer = viewerRef.current
      const canvas = canvasRef.current

      if (viewer && canvas) {
        for (const [userId, el] of cursorEls.current) {
          const peer = peersRef.current.get(userId)
          if (!peer || peer.x === null || peer.y === null) {
            el.style.opacity = "0"
            continue
          }
          const screen = imageToScreen(viewer, canvas, peer.x, peer.y)
          if (!screen?.visible) {
            el.style.opacity = "0"
          } else {
            el.style.opacity = "1"
            el.style.transform = `translate(${screen.x}px, ${screen.y}px)`
          }
        }
      }

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [viewerRef, canvasRef, peersRef])

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden"
      }}
    />
  )
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
}
